import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "americana_cart_v1";
const DEFAULT_SHIPPING = 6.99;
export const FREE_SHIPPING_THRESHOLD = 75;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [tip, setTip] = useState(0);
  const [isPickup, setIsPickup] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (item) => {
    setItems((prev) => [...prev, { ...item, line_id: crypto.randomUUID() }]);
  };

  const removeItem = (line_id) => setItems((prev) => prev.filter((i) => i.line_id !== line_id));

  const updateQuantity = (line_id, qty) =>
    setItems((prev) =>
      prev.map((i) => (i.line_id === line_id ? { ...i, quantity: Math.max(1, qty) } : i))
    );

  const clear = () => { setItems([]); setTip(0); setIsPickup(false); };

  const subtotal = useMemo(
    () =>
      items.reduce((s, i) => {
        const base = i.unit_price || 0;
        return s + base * (i.quantity || 1);
      }, 0),
    [items]
  );

  const shipping = useMemo(() => {
    if (items.length === 0) return 0;
    if (isPickup) return 0;
    // Items excluded from free shipping always pay their fee and don't count toward
    // the free-shipping threshold; eligible items ship free once their subtotal >= threshold.
    const eligibleSubtotal = items.reduce(
      (s, i) => s + (i.exclude_from_free_shipping ? 0 : (i.unit_price || 0) * (i.quantity || 1)),
      0
    );
    const freeEarned = eligibleSubtotal >= FREE_SHIPPING_THRESHOLD;
    return items.reduce((s, i) => {
      const fee = i.shipping_fee != null ? i.shipping_fee : DEFAULT_SHIPPING;
      const qty = i.quantity || 1;
      if (i.exclude_from_free_shipping) return s + fee * qty;
      return s + (freeEarned ? 0 : fee * qty);
    }, 0);
  }, [items, isPickup]);

  const discountableSubtotal = useMemo(
    () =>
      items.reduce(
        (s, i) => s + (i.exclude_from_discounts ? 0 : (i.unit_price || 0) * (i.quantity || 1)),
        0
      ),
    [items]
  );

  const freeShipEligibleSubtotal = useMemo(
    () =>
      items.reduce(
        (s, i) => s + (i.exclude_from_free_shipping ? 0 : (i.unit_price || 0) * (i.quantity || 1)),
        0
      ),
    [items]
  );

  const count = useMemo(() => items.reduce((s, i) => s + (i.quantity || 1), 0), [items]);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clear, subtotal, shipping, discountableSubtotal, freeShipEligibleSubtotal, tip, setTip, isPickup, setIsPickup, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
